import { useNavigate } from 'react-router-dom';
import { Button } from '@theraply/ui';
import { CheckCircleIcon, EnvelopeIcon, ClockIcon, DocumentCheckIcon } from '@heroicons/react/24/outline';

export function RegistrationSuccess() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8 sm:p-12">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
              <CheckCircleIcon className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="mt-6 text-3xl font-bold text-gray-900">Application Submitted!</h1>
            <p className="mt-4 text-lg text-gray-600">
              Thank you for applying to join Theraply as a therapist. Your application has been
              successfully submitted and is now under review.
            </p>
          </div>

          <div className="mt-10 space-y-6">
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">What happens next?</h2>

              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100">
                      <EnvelopeIcon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900">
                      Confirmation Email
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      You'll receive a confirmation email shortly with your application details
                      and a tracking number.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-purple-100">
                      <DocumentCheckIcon className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900">
                      Credential Verification
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Our compliance team will verify your professional credentials and review
                      your uploaded documents.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-amber-100">
                      <ClockIcon className="h-6 w-6 text-amber-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900">
                      Review Timeline
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      The review process typically takes 2-3 business days. You'll be notified
                      via email once your application has been reviewed.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-blue-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Need to update your application?</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      If you need to update any information or have questions about your application,
                      please contact our support team at{' '}
                      <a href="mailto:support@theraply.com" className="underline font-medium">
                        support@theraply.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Button
              variant="primary"
              fullWidth
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              fullWidth
              onClick={() => navigate('/login')}
            >
              Return to Login
            </Button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Questions about your application?{' '}
            <a
              href="mailto:support@theraply.com"
              className="font-medium text-primary-600 hover:text-primary-700"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
